import type { FlagsType } from '@iheartjane/feature-flags';
import {
  featureFlagProperties,
  hasMixpanelDeviceIdPrefix,
  normalizeMixpanelPropertyValues,
} from '@iheartjane/tracking';
/* eslint-disable import-x/no-named-as-default-member */
import mixpanelBrowser from 'mixpanel-browser';

import type { MixpanelEvent } from './Events';

let initialized = false;

const ANALYTICS_TAGS = ['utm_source', 'utm_medium', 'utm_campaign'];

/**
 * Initialize Mixpanel SDK. Should be called as early as possible in entry.client.tsx.
 * This only does the core init - call registerMixpanelIdentity() after hydration
 * to set user identity and super properties.
 */
export function initMixpanel(projectToken: string, debug = false) {
  if (initialized || typeof window === 'undefined') {
    return;
  }

  mixpanelBrowser.init(projectToken, {
    debug,
    ignore_dnt: debug,
    persistence: 'localStorage',
  });
  initialized = true;
}

type MixpanelIdentityConfig = {
  buildVersion: string;
  featureFlags?: FlagsType;
  operatorId: number;
  operatorName: string;
  pluginVersion?: string;
  storeCity: string;
  storeId: number;
  storeName: string;
  storeState: string;
  /** Signed-in Jane user id; absent for guests, who are never identified. */
  userId?: string;
};

/**
 * Register user identity and super properties with Mixpanel.
 * Should be called in a useEffect after the app has hydrated and loader data is available.
 */
export function registerMixpanelIdentity({
  buildVersion,
  featureFlags,
  operatorId,
  operatorName,
  pluginVersion,
  storeCity,
  storeId,
  storeName,
  storeState,
  userId,
}: MixpanelIdentityConfig) {
  if (!initialized) {
    return;
  }

  if (userId) {
    mixpanelBrowser.identify(userId);
  }

  mixpanelBrowser.register({
    app: 'bloom',
    build: buildVersion.substring(0, 7),
    featureFlags: featureFlagProperties(featureFlags),
    operatorId,
    'Operator Name': operatorName.toLowerCase(),
    pluginVersion,
    storeCity: storeCity.toLowerCase(),
    storeId,
    storeName: storeName.toLowerCase(),
    storeState: storeState.toLowerCase(),
  });
}

export function resetMixpanelIdentity() {
  if (initialized) {
    mixpanelBrowser.reset();
  }
}

/**
 * Migrates an anonymous shopper off a stale, identify-set distinct id: resets
 * Mixpanel so the SDK regenerates a native `$device:` id. Self-terminating — a
 * reset id is no longer stale, so this is a no-op on every subsequent load.
 */
export function reidentifyStaleGuest(jdid?: string) {
  if (!hasStaleAnonymousIdentity()) {
    return;
  }

  resetMixpanelIdentity();
  // reset() clears super properties, so restore the jdid super property.
  if (jdid) {
    registerJdid(jdid);
  }
}

/**
 * A guest id is "stale" when it lacks Mixpanel's native device-id prefix —
 * meaning it was set via `identify()` rather than auto-generated.
 */
function hasStaleAnonymousIdentity(): boolean {
  if (!initialized) {
    return false;
  }

  const distinctId = mixpanelBrowser.get_distinct_id();
  return !!distinctId && !hasMixpanelDeviceIdPrefix(distinctId);
}

export function track({ event, ...rest }: MixpanelEvent) {
  if (!initialized) {
    return;
  }

  try {
    const normalizedProperties = normalizeMixpanelPropertyValues(rest);
    mixpanelBrowser.track(event, normalizedProperties);
  } catch (e) {
    console.warn('Mixpanel tracking error: ', e);
  }
}

export function registerJdid(jdid: string) {
  if (initialized && jdid) {
    mixpanelBrowser.register({ janeDeviceId: jdid });
  }
}

/**
 * Bloom's Mixpanel distinct id, forwarded to the checkout iframe so guest
 * checkout events aren't tracked under the iframe's separate session.
 */
export function getMixpanelDistinctId(): string | null {
  if (!initialized) {
    return null;
  }

  return mixpanelBrowser.get_distinct_id() ?? null;
}

export type StoreSuperProperties = {
  storeCity: string;
  storeId: number;
  storeName: string;
  storeState: string;
};

/**
 * Register store-related super properties. Called whenever the pinned store
 * changes — via the StoreSwitcher, initial server-side resolution, or any
 * URL/slug/cookie-driven change picked up in loader data.
 */
export function registerSuperPropertiesStore({
  storeCity,
  storeId,
  storeName,
  storeState,
}: StoreSuperProperties) {
  mixpanelBrowser.register({
    storeCity: storeCity.toLowerCase(),
    storeId,
    storeName: storeName.toLowerCase(),
    storeState: storeState.toLowerCase(),
  });
}

export function getMixpanelAnalyticsTags(): string | null {
  if (!initialized) {
    return null;
  }

  const params = new URLSearchParams();

  for (const tag of ANALYTICS_TAGS) {
    const value = mixpanelBrowser.get_property(tag);
    if (value) {
      params.append(tag, value);
    }
  }

  return params.toString() || null;
}
/* eslint-enable import-x/no-named-as-default-member */
